// Client for the curriculum API (`{node-api}/api/v1/curriculum`), used by the react-query hooks in
// `useCurriculum.ts`. Headers mirror SagaHelper's, like the admissions client
// (`app/pages/admissions/lib/api.ts`). Shapes follow the backend handoff
// (curriculum-books-frontend.md, 04-Nov-2026).
import {getTenantDomain} from 'common/HelperFunctions'
import {SECRET_KEY, base_url} from 'common/constants'
import {redirectToLogout} from 'setup/redux/Sagas/SagaHelper'
import store from 'setup/redux/Store'

const ROOT = 'curriculum'

/** A failed call: HTTP status and the API's `message` (shown to the user for 400/409). */
export type CurriculumApiError = {status: number; message: string}

export const isApiError = (error: unknown): error is CurriculumApiError =>
  typeof error === 'object' && error !== null && 'status' in error && 'message' in error

const buildHeaders = (json = true): Record<string, string> => {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    platform: 'web',
    platformKey: SECRET_KEY.web,
    version: '1.0.0',
    domain: getTenantDomain(),
  }
  if (json) headers['Content-Type'] = 'application/json'
  const {accessToken} = store.getState().userReducer
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`
  return headers
}

/** Only the auth middleware answers 401, so a signed-in 401 is an expired session. */
const signOutOnExpiredSession = (response: Response) => {
  if (response.status === 401 && store.getState().userReducer.accessToken) redirectToLogout()
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${base_url}${ROOT}${path}`, {
    method,
    headers: buildHeaders(),
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  let json: {message?: string; status?: string; result?: T} = {}
  try {
    json = await response.json()
  } catch {
    // non-JSON error body
  }
  if (!response.ok || json.status !== 'true') {
    signOutOnExpiredSession(response)
    throw {status: response.status, message: json.message ?? response.statusText ?? 'Request failed'} as CurriculumApiError
  }
  return json.result as T
}

// ---------- Types (API shapes) ----------

export type CurriculumStatus = {
  enabled: boolean
  can_manage: boolean
  can_ask: boolean
  route?: string
  storage?: string
}

export type BookStatus =
  | 'uploaded'
  | 'detecting'
  | 'needs_structure'
  | 'extracting'
  | 'in_review'
  | 'publishing'
  | 'ready'
  | 'failed'

export type ApiBook = {
  id: number
  title: string
  subject: string
  grade_level: string
  publisher_name: string
  language?: string
  status?: BookStatus
  stage?: string | null
  progress?: {done: number; total: number}
  error?: string | null
  interrupted?: boolean
  can_resume?: boolean
  heartbeat_at?: string | null
  pages?: number
  pages_reviewed?: number
  pages_with_errors?: number
  created_at?: string
  updated_at?: string
}

export type StorageInfo = {
  mode: string
  bucket: string | null
  bucketSource?: string
  region?: string
  prefix?: string
  localFolder?: string | null
  reachable: boolean
  error: string | null
  configured?: {env: string; purpose: string; bucket: string | null}[]
  server?: string
}

export type UnitKind = 'unit' | 'review'

export type StructureUnit = {
  unit_no: number | null
  title: string
  kind: UnitKind
  start_page: number
  end_page?: number
  themes?: string[]
  skills?: string[]
}

export type BlockType =
  | 'story'
  | 'poem'
  | 'dialogue'
  | 'definition'
  | 'vocabulary'
  | 'example'
  | 'table'
  | 'diagram'
  | 'exercise'
  | 'check_question'
  | 'warm_up'
  | 'learning_outcomes'
  | 'teacher_note'
  | 'picture_description'
  | 'other'

/** Types a printed box name can be mapped to when confirming the structure. */
export const LABEL_TYPES = [
  'definition',
  'vocabulary',
  'example',
  'exercise',
  'check_question',
  'warm_up',
  'learning_outcomes',
  'teacher_note',
  'diagram',
  'table',
  'other',
] as const

export type BookStructure = {
  confirmed: boolean
  pdf_pages: number
  page_offset: number
  contents_pages?: number[]
  notes: string[]
  units: StructureUnit[]
  profile: {labels: Record<string, string>; teacher_labels?: string[]}
}

export type PageFlag = {code: string; severity: 'error' | 'review' | 'warn'; detail: string}

export type ReviewStatus = 'pending' | 'approved' | 'edited'

export type PageSummary = {
  pdf_page: number
  printed_page: number | null
  unit_no: number | null
  page_kind: string
  review_status: ReviewStatus
  blocks: number
  flags: PageFlag[]
  reviewed_by: unknown
  reviewed_at: string | null
}

export type PageBlock = {
  block_type: BlockType | string
  label: string | null
  heading: string | null
  section_path: string[]
  activity_ref: string | null
  origin: 'printed' | 'described' | string
  audience: 'all' | 'teacher' | string
  text: string
  has_non_latin_script?: boolean
}

export type ExtractedPage = {
  printed_page: number | null
  page_kind: string
  unit_no: number | null
  continues_section?: boolean
  blocks: PageBlock[]
}

export type PageDetail = {
  pdf_page: number
  printed_page: number | null
  unit_no: number | null
  review_status: ReviewStatus
  flags: PageFlag[]
  image_url: string | null
  extracted: ExtractedPage
}

export type ApiCitation = {label: string; unit_no: number | null; printed_page: number; page_image_url?: string | null}

export type ChatResult = {
  answer: string
  presentation?: 'curriculum_answer' | 'clarify' | string
  data?: {
    from_book: string | null
    added_explanation: string | null
    citations: ApiCitation[]
    not_in_book: boolean
  }
}

export type ChatOptions = {
  book_id: number
  mode: 'ask' | 'explain'
  reply_language: 'en' | 'ur'
  conversationId?: string
  image_key?: string | null
}

/** A unit for learners (`GET /books/:id/units`). Reviews have `unit_no: null`. */
export type BookUnit = {
  unit_no: number | null
  title: string
  kind: 'unit' | 'review'
  start_page: number
  end_page: number
  /** Signed JPEG (400 px wide) of the unit's opening picture; ~15 min; null = no picture. */
  cover_image_url: string | null
  /** API path that redirects to a fresh cover (needs the auth headers). */
  cover_path: string
  sections: {title: string; printed_page: number}[]
}

export type BookUnits = {
  book_id: number
  page_offset: number
  units: BookUnit[]
  starter_questions: {text: string; printed_page: number}[]
}

type UnitStat = {unit_no: number | null; kind: string; title: string; start_page: number; questions: number; explain_requests: number}
type PageStat = {printed_page: number; unit_no: number | null; title: string; questions: number; explain_requests: number}

/** Usage of a book in this school (`GET /books/:id/insights`, school admins). No student data. */
export type BookInsights = {
  book_id: number
  section_id: number | null
  days: number
  from: string
  questions_total: number
  questions_this_month: number
  students_asking: number
  photos_total: number
  /** Fraction (0.14 = 14%). */
  urdu_share: number
  not_in_book_count: number
  /** Every unit, including those with no questions. */
  by_unit: UnitStat[]
  top_pages: PageStat[]
  clearer_explanation_pages: PageStat[]
  top_questions: {text: string; count: number}[]
}

export type UploadTicket = {uploadURL: string; key: string; mode: string}

export type NewBook = {
  file_key: string
  title: string
  subject: string
  grade_level: string
  publisher_name: string
  language: string
}

// ---------- Endpoints ----------

export const curriculumApi = {
  status: () => request<CurriculumStatus>('GET', '/status'),
  storage: () => request<StorageInfo>('GET', '/settings/storage'),
  books: () => request<ApiBook[]>('GET', '/books'),
  book: (id: number) => request<ApiBook>('GET', `/books/${id}`),
  bookUploadUrl: (fileName: string) =>
    request<UploadTicket>('POST', '/books/upload-url', {file_name: fileName, content_type: 'application/pdf'}),
  createBook: (book: NewBook) => request<ApiBook>('POST', '/books', book),
  deleteBook: (id: number) => request<{id: number}>('DELETE', `/books/${id}`),
  resume: (id: number) => request<ApiBook>('POST', `/books/${id}/resume`),
  structure: (id: number) => request<BookStructure>('GET', `/books/${id}/structure`),
  confirmStructure: (
    id: number,
    body: {page_offset: number; units: Omit<StructureUnit, 'end_page' | 'themes' | 'skills'>[]; profile: {labels: Record<string, string>}}
  ) => request<ApiBook>('PUT', `/books/${id}/structure`, body),
  pages: (id: number) => request<PageSummary[]>('GET', `/books/${id}/pages`),
  page: (id: number, pdfPage: number) => request<PageDetail>('GET', `/books/${id}/pages/${pdfPage}`),
  savePage: (id: number, pdfPage: number, extracted: ExtractedPage) =>
    request<{pdf_page: number; review_status: ReviewStatus}>('PUT', `/books/${id}/pages/${pdfPage}`, {extracted}),
  approve: (id: number, pages: number[] | 'all') =>
    request<{approved: number}>('POST', `/books/${id}/pages/approve`, pages === 'all' ? {all: true} : {pages}),
  publish: (id: number) => request<ApiBook>('POST', `/books/${id}/publish`),
  units: (id: number) => request<BookUnits>('GET', `/books/${id}/units`),
  insights: (id: number, days?: number) =>
    request<BookInsights>('GET', `/books/${id}/insights${days ? `?days=${days}` : ''}`),
  photoUploadUrl: (contentType: string) => request<UploadTicket>('POST', '/photo-upload-url', {content_type: contentType}),
  chat: (query: string, conversationHistory: {query: string; answer: string}[], options: ChatOptions) =>
    request<ChatResult>('POST', '/chat', {query, conversationHistory, options}),
}

/**
 * A citation's page image when its signed URL has expired: the endpoint redirects to a fresh
 * image but needs the auth headers, so it is fetched as a blob (callers revoke the URL).
 */
export const fetchPageImage = async (bookId: number, printedPage: number): Promise<string> => {
  const response = await fetch(`${base_url}${ROOT}/books/${bookId}/pages/${printedPage}/image`, {
    headers: buildHeaders(false),
  })
  if (!response.ok) throw {status: response.status, message: response.statusText} as CurriculumApiError
  return URL.createObjectURL(await response.blob())
}

/**
 * A unit cover when its signed URL has expired: `cover_path` (an API path such as
 * `/api/v1/curriculum/books/5/units/10/cover`) redirects to a fresh image and needs the auth
 * headers, so it is fetched as a blob (callers revoke the URL).
 */
export const fetchCover = async (coverPath: string): Promise<string> => {
  const path = coverPath.replace(/^\/?api\/v1\//, '')
  const response = await fetch(`${base_url}${path}`, {headers: buildHeaders(false)})
  if (!response.ok) throw {status: response.status, message: response.statusText} as CurriculumApiError
  return URL.createObjectURL(await response.blob())
}

/**
 * PUTs a file to a signed S3 URL with progress. Not through axios/fetch with our headers: S3
 * rejects a second auth mechanism (see `common/helpers/signedUpload.ts`), and XHR reports upload
 * progress.
 */
export const putFile = (url: string, file: Blob, contentType: string, onProgress?: (percent: number) => void) =>
  new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', url)
    xhr.setRequestHeader('Content-Type', contentType)
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) onProgress?.(Math.round((event.loaded / event.total) * 100))
    }
    xhr.onload = () =>
      xhr.status >= 200 && xhr.status < 300
        ? resolve()
        : reject({status: xhr.status, message: xhr.statusText || 'Upload failed'} as CurriculumApiError)
    xhr.onerror = () => reject({status: 0, message: 'Upload failed'} as CurriculumApiError)
    xhr.send(file)
  })
